import throttle from 'lodash.throttle';
import g from '#core/global';
import load from '#core/load';
import layout from '#core/layout';
import util from '#core/util';
import select from '#element/select/select';


const state = window.teal_state = window.teal_state || {};

const is_active_attr = 'data-is-active';
const is_obsolete_attr = 'data-is-obsolete';
const criteria_$ = '.filter__criteria';
const select_$ = '.select__select';
const desktop_breakpoint = 'b5';
const abort_controllers = new WeakMap();

let has_filter = false;
let is_mobile = false;
let replace_criteria_ui = false;

const container_c = 'filter';
const container_$ = `.${container_c}`;
const root_$ = '.filter__root';
const group_id_attr = 'data-filter-group-id';
const response_c = 'filter__response';
const response_$ = `.${response_c}`;
const results_$ = '.feed__items';
const result_count_attr = 'data-result-count';
const result_count_$ = '.count';
const load_more_btn_$ = '.filter__load-more__button';
const pagination_$ = '.filter__pagination';
const pagination_link_$ = '.filter__pagination__link';
const numbered_pagination_container_$ = `${container_$}[data-pagination-style="pagination"]`;
const filter_checkbox_$ = 'input[type="checkbox"]';
const filter_radio_$ = 'input[type="radio"]';
const toggle_input_option_$ = '.toggle-input-option';
const option_children_container_$ = '.toggle-input-option__children';
const search_field_container_$ = '.field.\\:type-search';
const search_field_submit_$ = '[type="submit"]';
const search_field_$ = `input[type="search"]`;
const sort_select_$ = '.select--sort .select__select';
const filter_id_attr = 'data-filter-id';
const keywords_remote_container_$ = '[data-filter-keywords-remote]';
const keywords_remote_$ = 'input[type="search"]';
const applied_filters_$ = '.filter__applied-filters';
const applied_filter_id_attr = 'data-match-id';
const reset_$ = '.filter__reset';
const disabled_attr = 'disabled';
const sort_param = 'sort';
const key_search_delay = 500;
const href_attr = 'href';
const input_name_attr = 'name';
const input_name_temp_attr = 'data-input-name';
const fields_secondary_$ = '.filter__fields--secondary';
const field_container_secondary_$ = '.filter__field-container--secondary';
const field_location_panel_$ = '.filter__field-container--panel';
const field_location_body_$ = '.filter__field-container--body';

const download_btn_$ = '.filter__download-results';
const download_param = 'download';
const download_attr = 'download';
const download_prefix = 'search-results-';

let search_timeout = null;
const previous_search_texts = {};


const module = {
	request_e: 'filter-change-request',
	start_e: 'filter-change-start',
	end_e: 'filter-change-end',
	name_e: 'filter-update-field-names',
	
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('filter')) return false;
		
		load.content(() => {
			has_filter = assign_elements();
			if (!has_filter) return false;
			
			assign_resize_events();
		});
		load.all(() => {
			has_filter = assign_elements();
			if (!has_filter) return false;
		});
	},
	
	
	/**
	 * Update input names
	 */
	updateInputNames() {
		update_input_names();
	},
	
};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_filter) return true;
	
	document.addEventListener(module.request_e, e => {
		const $container = e.detail.$source.closest(container_$);
		if (!$container) return false;
		
		filter_results(null, $container);
	});
	
	// Update input names
	document.addEventListener(module.name_e, e => {
		update_input_names();
	});
	
	// Update on select change
	document.addEventListener(g.change_e, e => {
		const $select = e.target.closest(`${container_$} ${select_$}`);
		if (!$select) return;
		
		const $container = $select.closest(container_$);
		filter_results(e, $container);
	});
	
	// Update selected hierarchical options on checkbox/radio change
	document.addEventListener(g.change_e, e => {
		const $option = e.target.closest(`${criteria_$} :is(${filter_checkbox_$}, ${filter_radio_$})`);
		if (!$option) return;
		
		check_hierarchical_options(e, $option);
		
		// Introduce a small delay to allow updating field names before filtering
		const $container = $option.closest(container_$);
		setTimeout(() => {
			filter_results(e, $container);
		}, 50);
	});
	
	// Update on form submit
	document.addEventListener(g.submit_e, e => {
		const $criteria = e.target.closest(criteria_$);
		if (!$criteria) return;
		
		e.preventDefault();
		const $container = $criteria.closest(container_$);
		filter_results(e, $container);
	});
	
	// Load more on button click
	document.addEventListener(g.click_e, e => {
		const $load_more_btn = e.target.closest(load_more_btn_$);
		if (!$load_more_btn) return;
		
		const $container = $load_more_btn.closest(container_$);
		if (!$container) return false;
		
		load_more(e, $container, $load_more_btn);
	});

	// Replace results on pagination click
	document.addEventListener(g.click_e, e => {
		const $pagination_link = e.target.closest(pagination_link_$);
		if (!$pagination_link) return;

		const $container = $pagination_link.closest(container_$);
		if (!$container) return false;

		paginate(e, $container, $pagination_link);
	});

	// Restore asynchronously loaded pages when using browser history
	window.addEventListener('popstate', () => {
		document.querySelectorAll(numbered_pagination_container_$).forEach($container => {
			$container.setAttribute(is_active_attr, g.true_s);
			$container.setAttribute(is_obsolete_attr, g.true_s);
			get_new_results($container, window.location.href, true, {
				update_history: false,
			});
		});
	});
	
	// Remove applied filter on click
	document.addEventListener(g.click_e, e => {
		if (!e.target.closest(applied_filters_$)) return;
		
		e.preventDefault();
		remove_filter(e);
	});
	
	// Reset on click
	document.addEventListener(g.click_e, e => {
		if (!e.target.closest(reset_$)) return;
		
		e.preventDefault();
		reset(e);
	});
	
	// Download results
	document.addEventListener(g.click_e, e => {
		const $button = e.target.closest(download_btn_$);
		if (!$button) return;
		
		e.preventDefault();
		const $container = $button.closest(container_$);
		filter_results(null, $container, {
			[download_param]: 1,
		});
	});
	
	// Update on search field input
	[g.input_e, g.keydown_e].forEach(event_name => {
		document.addEventListener(event_name, e => {
			const $search_field = e.target.closest(`${container_$} ${search_field_$}`);
			if (!$search_field) return;
			
			const $container = $search_field.closest(container_$);
			fire_update_from_keyword_search(e, $container);
		});
	});
	
	// Copy value from remote search field to local search field
	document.addEventListener(g.input_e, e => {
		const $search_field_remote = e.target.closest(`${keywords_remote_container_$} ${keywords_remote_$}`);
		if (!$search_field_remote) return;
		
		const $remote_container = e.target.closest(keywords_remote_container_$);
		const filter_id = $remote_container.getAttribute(group_id_attr);
		if (!filter_id) return false;
		
		const $container = document.querySelector(`${container_$}[${group_id_attr}="${filter_id}"]`);
		const $search_field = $container.querySelector(search_field_$);
		$search_field.value = e.target.value;
		$search_field.dispatchEvent(new InputEvent(g.input_e, e));
	});
	
	// Submit form on remote search field enter
	document.addEventListener(g.keydown_e, e => {
		const $search_field_remote = e.target.closest(`${keywords_remote_container_$} ${keywords_remote_$}`);
		if (!$search_field_remote) return;
		if (e.key !== 'Enter') return false;
		
		const $remote_container = e.target.closest(keywords_remote_container_$);
		const filter_id = $remote_container.getAttribute(group_id_attr);
		if (!filter_id) return false;
		
		const $container = document.querySelector(`${container_$}[${group_id_attr}="${filter_id}"]`);
		const $search_field = $container.querySelector(search_field_$);
		$search_field.dispatchEvent(new KeyboardEvent(g.keydown_e, e));
	});
	
	// Update on remote submit button click
	document.addEventListener(g.click_e, e => {
		const $remote_submit = e.target.closest(`${keywords_remote_container_$} ${search_field_submit_$}`);
		if (!$remote_submit) return;
		
		const $remote_container = e.target.closest(keywords_remote_container_$);
		const filter_id = $remote_container.getAttribute(group_id_attr);
		if (!filter_id) return;
		
		const $container = document.querySelector(`${container_$}[${group_id_attr}="${filter_id}"]`);
		fire_update_from_keyword_search(null, $container, true);
	});
	
	// Copy initial value from local search field to remote search field
	document.querySelectorAll(keywords_remote_container_$).forEach($remote_container => {
		const filter_id = $remote_container.getAttribute(group_id_attr);
		if (!filter_id) return false;
		
		const $container = document.querySelector(`${container_$}[${group_id_attr}="${filter_id}"]`);
		if (!$container) return false;
		
		const $search_field = $container.querySelector(search_field_$);
		const $search_field_remote = $remote_container.querySelector(keywords_remote_$);
		
		if ($search_field && $search_field_remote) {
			$search_field_remote.value = $search_field.value;
		}
	});
	
	// TODO: make this work, right now we don't have succinct paired fields
	// with their own submit buttons
	// if ($search_field_submit) {
	// 	$search_field_submit.addEventListener(g.click_e, e => {
	// 		e.preventDefault();
	// 		$criteria.requestSubmit();
	// 	});
	// }
	
	// Update ranges
	document.addEventListener(g.change_e, e => {
		const $range_field = e.target.closest('.range-group input[type="range"]');
		if (!$range_field) return;
		
		const $container = $range_field.closest(container_$);
		if (!$container) return;
		
		setTimeout(() => {
			filter_results(e, $container);
		}, 200);
	});
	
	// update_sort_select($root);
	return true;
}


/**
 * Assign resize events
 */
function assign_resize_events() {
	const resize_callbacks = [
		move_fields,
	];
	resize_callbacks.forEach(callback => callback());
	
	window.addEventListener(g.resize_e, throttle(() => {
		resize_callbacks.forEach(callback => callback());
	}, 150));
}


/**
 * Fire update from from keyword search
 * @param {event} e: Only pass this if it is an input or keyboard event
 * @param {element} $container
 * @param {boolean} fire_immediately
 * @return {boolean}
 */
function fire_update_from_keyword_search(e, $container, fire_immediately = false) {
	const $criteria = $container.querySelector(criteria_$);
	if (!$criteria) return;
	
	clearTimeout(search_timeout);
	const key_name = e && e.key;
	const current_search_text = e.target.value.trim();
	const filter_id = $container.getAttribute(group_id_attr);
	
	if (fire_immediately || key_name === 'Enter') {
		e && e.preventDefault();
		$criteria.requestSubmit();
		previous_search_texts[filter_id] = current_search_text;
	} else {
		search_timeout = setTimeout(() => {
			if (current_search_text !== previous_search_texts[filter_id]) {
				$criteria.requestSubmit();
				previous_search_texts[filter_id] = current_search_text;
			}
		}, key_search_delay);
	}
}


/**
 * Load more results
 * @param {event} e
 * @param {element} $container
 * @param {element} $button
 * @return {boolean}
 */
function load_more(e, $container, $button) {
	e.preventDefault();
	if ($button.hasAttribute(disabled_attr)) return false;
	
	$container.setAttribute(is_active_attr, g.true_s);
	$container.setAttribute(is_obsolete_attr, g.false_s);
	$button.setAttribute(disabled_attr, g.true_s);
	get_new_results($container, $button.href, false);
}


/**
 * Replace results with a selected page.
 * @param {event} e
 * @param {element} $container
 * @param {element} $link
 */
function paginate(e, $container, $link) {
	if (
		e.button !== 0
		|| e.metaKey
		|| e.ctrlKey
		|| e.shiftKey
		|| e.altKey
	) return;

	e.preventDefault();

	$container.setAttribute(is_active_attr, g.true_s);
	$container.setAttribute(is_obsolete_attr, g.true_s);
	get_new_results($container, $link.href, true, {
		scroll_to_results: true,
	});
}


/**
 * Reset filters
 * @param {event} e
 * @return {boolean}
 */
function reset(e) {
	e.preventDefault();
	
	let $reset = e.target.closest(reset_$);
	if (!$reset.hasAttribute(href_attr)) {
		$reset = $reset.querySelector(`[${href_attr}]`);
	}
	
	const $container = $reset.closest(container_$);
	if (!$container) return false;
	
	$container.setAttribute(is_active_attr, g.true_s);
	$container.setAttribute(is_obsolete_attr, g.true_s);
	
	// Disable load more button
	const $load_more_btn = $container.querySelector(load_more_btn_$);
	if ($load_more_btn) {
		$load_more_btn.setAttribute(disabled_attr, g.true_s);
	}
	
	// Clear search field
	const filter_id = $container.getAttribute(group_id_attr);
	const $search_field = $container.querySelector(search_field_$);
	if ($search_field) {
		$search_field.value = '';
	}
	const $search_field_remote_container = document.querySelector(`${keywords_remote_container_$}[${group_id_attr}="${filter_id}"]`);
	if ($search_field_remote_container) {
		const $search_field_remote = $search_field_remote_container.querySelector(keywords_remote_$);
		if ($search_field_remote) {
			$search_field_remote.value = '';
		}
	}
	
	// Reset all filter checkboxes
	const $checkboxes = $container.querySelectorAll(filter_checkbox_$);
	$checkboxes.forEach($checkbox => {
		if ($checkbox.checked) {
			$checkbox.click();
		}
	});
	
	// Reset all filter radio buttons
	const radio_group_names = [];
	const $radios = $container.querySelectorAll(filter_radio_$);
	$radios.forEach($radio => {
		const input_name = get_input_name($radio);
		if (radio_group_names.includes(input_name)) return true;
		
		radio_group_names.push(input_name);
	});
	
	radio_group_names.filter(x => x).forEach(radio_group_name => {
		const $first_option = Array.from($radios).find($radio => {
			const input_name = get_input_name($radio);
			return input_name === radio_group_name;
		});
		if (!$first_option) return false;
		if ($first_option.checked) return false;
		
		$first_option.click();
	});
	
	// Reset all select elements
	const $selects = $container.querySelectorAll(select_$);
	$selects.forEach($select => {
		select.reset($select);
	});
	
	// Reset range fields
	const $range_fields = $container.querySelectorAll('input[type="range"]');
	$range_fields.forEach($range_field => {
		const is_min = $range_field.classList.contains('range-group__field--min');
		if (is_min) {
			$range_field.value = $range_field.min;
		} else {
			$range_field.value = $range_field.max;
		}
	});
	
	get_new_results($container, $reset.href, true);
}


/**
 * Get input name
 * @param {element} $input
 * @return {string}
 */
function get_input_name($input) {
	return $input.name || $input.getAttribute(input_name_temp_attr);
}


/**
 * Filter results
 * @param {event} e
 * @param {element} $container
 * @param {object} additional_data
 * @return {boolean}
 */
function filter_results(e = null, $container, additional_data = {}) {
	document.dispatchEvent(new CustomEvent(module.start_e));
	
	if (e) {
		e.preventDefault();
	}
	
	// Update container status
	$container.setAttribute(is_active_attr, g.true_s);
	$container.setAttribute(is_obsolete_attr, g.true_s);
	
	// Gather all form data and remove empty values
	const $criteria = $container.querySelector(criteria_$);
	const form_data = new FormData($criteria);
	const all_params = new URLSearchParams(form_data);
	const empty_params = [];
	for (const [key, value] of all_params.entries()) {
		if (value === '') {
			empty_params.push(key);
		}
	}
	for (const key of empty_params) {
		all_params.delete(key);
	}
	
	// Remove superfluous range parameters
	const $range_groups = $container.querySelectorAll('.range-group');
	$range_groups.forEach($range_group => {
		const $range_min_field = $range_group.querySelector('.range-group__field--min');
		const $range_max_field = $range_group.querySelector('.range-group__field--max');
		const min_name = $range_min_field.name;
		const min_value = $range_min_field.value;
		const min_min = $range_min_field.min;
		const max_name = $range_max_field.name;
		const max_value = $range_max_field.value;
		const max_max = $range_max_field.max;
		if (
			min_value !== min_min
			|| max_value !== max_max
		) return false;
		
		all_params.delete(min_name);
		all_params.delete(max_name);
	});
	
	// Add sorting data to params
	const $sort_select = $container.querySelector(sort_select_$);
	if ($sort_select) {
		all_params.append(sort_param, $sort_select.value);
	}
	
	// Add additional data
	for (let [key, value] of Object.entries(additional_data)) {
		all_params.append(key, value);
	}
	
	// Construct URL
	const param_string = all_params.toString();
	const action_url = $criteria.getAttribute('action');
	const url = [
		action_url,
		param_string,
	].filter(x => x).join('?');
	
	get_new_results($container, url, true);
	
	if (
		download_param in additional_data
		&& additional_data[download_param]
	) return true;
}


/**
 * Get new results
 * @param {element} $container
 * @param {string} url
 * @param {boolean} replace
 * @param {object} options
 */
function get_new_results($container, url, replace = false, options = {}) {
	const {
		update_history = true,
		scroll_to_results = false,
	} = options;
	const active_abort_controller = abort_controllers.get($container);
	if (active_abort_controller) {
		active_abort_controller.abort();
	}
	const abort_controller = new AbortController();
	abort_controllers.set($container, abort_controller);
	
	fetch(url, {
		signal: abort_controller.signal,
	})
		.then(response => response.text())
		.then(text => {
			if (util.isValidJSON(text)) {
				const download_data = JSON.parse(text);
				download_csv(download_data);
			} else {
				update_results($container, text, replace);
				if (update_history) {
					window.history.pushState({}, '', url);
				}
				if (scroll_to_results) {
					const $response = $container.querySelector(response_$);
					if ($response) {
						const reduce_motion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
						$response.scrollIntoView({
							behavior: reduce_motion ? 'auto' : 'smooth',
							block: 'start',
						});
					}
				}
			}
		})
		.catch(error => {
			if (error.name === 'AbortError') return null;
			
			console.error('Error:', error);
		})
		.finally(() => {
			if (abort_controllers.get($container) === abort_controller) {
				abort_controllers.delete($container);
			}
		})
	;
}


/**
 * Update results
 * @param {element} $container
 * @param {string} html
 * @param {boolean} replace_results
 */
function update_results($container, html, replace_results = false) {
	let $data = new DOMParser().parseFromString(html, 'text/html');
	let $data_response = null;
	
	// Isolate results belonging to filter group
	const filter_group_id = $container.getAttribute(group_id_attr);
	$data = $data.querySelector(`${container_$}[${group_id_attr}="${filter_group_id}"]`);
	
	// Create placeholder content if instance was missing from response
	if ($data) {
		$data_response = $data.querySelector(response_$);
	} else {
		$data = document.createElement('div');
		$data.classList.add(container_c);
		$data.setAttribute(group_id_attr, filter_group_id);
	}
	
	// Create placeholder content if instance was missing from response
	if (!$data_response) {
		const no_results_html = `
			<article class="no-results-card">
				<h3 class="title">No Results</h3>
				<div class="abstract" data-content>
					Sorry, no results were found.
				</div>
			</article>
		`;
		
		$data_response = document.createElement('div');
		$data_response.classList.add(response_c);
		$data_response.innerHTML = no_results_html;
		
		$container.setAttribute(is_active_attr, g.false_s);
		$container.setAttribute(is_obsolete_attr, g.false_s);
		
		replace_results = true;
	}
	
	const $elements = Array.from($data_response.children);
	const fragment = new DocumentFragment();
	
	// Replace all results
	const $response = $container.querySelector(response_$);
	if (replace_results) {
		$response.innerHTML = '';
		$elements.forEach($element => {
			fragment.append($element);
		});
		$response.append(fragment);
	}
	
	// Append new results
	else {
		const $new_results_container = $data_response.querySelector(results_$);
		const $results = Array.from($new_results_container.children);
		$results.forEach($result => {
			fragment.append($result);
		});
		
		const $results_container = $response.querySelector(results_$);
		$results_container.append(fragment);
	}
	
	// Update ranges
	const $range_groups = $container.querySelectorAll('.range-group');
	$range_groups.forEach($range_group => {
		// range_group.updateUI($range_group);
	});
	
	// Update container status
	$container.setAttribute(is_active_attr, g.false_s);
	$container.setAttribute(is_obsolete_attr, g.false_s);
	
	// Update result count
	// TODO: set result count to 0 if $data came back empty
	const $container_result_counts = $container.querySelectorAll(`[${result_count_attr}]`);
	const $response_result_counts = $data.querySelectorAll(`[${result_count_attr}]`);
	Array.from($response_result_counts).every($response_result_count => {
		const count = parseInt($response_result_count.getAttribute(result_count_attr));
		if (isNaN(count)) return true;
		
		$container_result_counts.forEach($container_result_count => {
			$container_result_count.innerHTML = $response_result_count.innerHTML;
		});
		return false;
	});
	
	// Update attributes on load more button
	const $load_more_btn = $container.querySelector(load_more_btn_$);
	if ($load_more_btn) {
		const $next_load_more_btn = $data.querySelector(load_more_btn_$);
		if ($next_load_more_btn) {
			const next_href = $next_load_more_btn.getAttribute(href_attr);
			$load_more_btn.setAttribute(href_attr, next_href);
			$load_more_btn.removeAttribute(disabled_attr);
		} else {
			// Prevent duplicate results from loading
			$load_more_btn.removeAttribute(href_attr);
			$load_more_btn.setAttribute(disabled_attr, g.true_s);
		}
	}

	// Replace numbered pagination with the navigation from the response
	const $pagination = $container.querySelector(pagination_$);
	const $next_pagination = $data.querySelector(pagination_$);
	if ($pagination && $next_pagination) {
		$pagination.replaceWith($next_pagination);
	} else if ($pagination) {
		$pagination.remove();
	} else if ($next_pagination) {
		const $root = $container.querySelector(root_$);
		const $loading = $root.querySelector('.filter__loading');
		$root.insertBefore($next_pagination, $loading);
	}
	
	if (replace_criteria_ui) {
		const $container_criteria = $container.querySelector(criteria_$);
		const $response_criteria = $data.querySelector(criteria_$);
		if ($container_criteria && $response_criteria) {
			$container_criteria.innerHTML = $response_criteria.innerHTML;
		}
	}
	
	// Reinitialize reveal animations and other dynamic components
	window.dispatchEvent(new CustomEvent('reinitialize'));
	
	document.dispatchEvent(new CustomEvent(module.end_e));
}


/**
 * Check hierarchical options before filtering results
 * @param {event} e
 * @param {element} $option
 */
function check_hierarchical_options(e, $option) {
	const $option_container = $option.closest(toggle_input_option_$).parentNode;
	let $children = $option.closest(option_children_container_$);
	
	if ($children) {
		// $option is a child option
		// If $option is checked, uncheck its parent option
		const $parent = layout.getSibling($children, toggle_input_option_$);
		if ($option.checked) {
			$parent.querySelector(filter_checkbox_$).checked = false;
		}
	}
	
	else {
		$children = $option_container.querySelector(option_children_container_$);
		if ($children) {
			// $option is a parent option
			// If $option is checked, uncheck all child options
			$children.querySelectorAll(filter_checkbox_$).forEach($child => {
				$child.checked = false;
			});
		}
	}
}


/**
 * Remove filter
 * @param {event} e
 */
function remove_filter(e) {
	const $source = e.target.closest(`[${applied_filter_id_attr}]`);
	if (!$source) return false;
	
	const $container = $source.closest(container_$);
	if (!$container) return false;
	
	const target_id = $source.getAttribute(applied_filter_id_attr);
	
	// TODO: identify range applied filters
	const base_id = null;
	
	
	///////////// TODO: CLEAN THIS UP //////////////
	
	// ✅ Handle range input removal
	if (base_id === 'year_min' || base_id === 'year_max') {
		const $range_field = $container.querySelector(`[data-filter-id="${base_id}"]`);
		if (!$range_field) return false;
		
		const $range_wrapper = $range_field.closest('.range-group');
		if (!$range_wrapper) {
			console.warn('❌ rangeWrapper not found for [data-range-name="year"]');
			return;
		}
		
		const $inputMin = $range_wrapper.querySelector('.range-group__field--min');
		const $inputMax = $range_wrapper.querySelector('.range-group__field--max');
		
		// Only reset the relevant slider input
		if (base_id === 'year_min') {
			$inputMin.value = $inputMin.min;
		} else if (base_id === 'year_max') {
			$inputMax.value = $inputMax.max;
		}
		
		// Trigger visual update and form events
		window.updateRangeUI($range_wrapper, true);
		
		$source.remove();
		
		filter_results(null, $container);
		return;
	}
	
	///////////// TODO: OK YOU CAN STOP NOW //////////////
	
	
	const $target = $container.querySelector(`${filter_checkbox_$}[${filter_id_attr}="${target_id}"]`);
	if (!$target) {
		$source.remove();
		return false;
	}
	
	if ($target.checked) {
		$target.click();
	}
	$source.remove();
}


/**
 * Download CSV
 * @param {array} data
 */
function download_csv(data) {
	const headers = Object.keys(data[0]);
	const csv = [
		headers.join(','),
		...data.map(row => {
			return headers.map(field_name => {
				return JSON.stringify(row[field_name], (key, value) => {
					return (value !== null)
						? value
						: '';
				});
			}).join(',')
		}),
	].join('\r\n');
	
	const file_contents = 'data:text/json;charset=utf-8,' + encodeURIComponent(csv);
	const $hidden_download = document.createElement('a');
	$hidden_download.setAttribute(href_attr, file_contents);
	$hidden_download.setAttribute(download_attr,
		download_prefix + timestamp() + '.csv'
	);
	document.body.appendChild($hidden_download);
	$hidden_download.click();
	document.body.removeChild($hidden_download);
}


/**
 * Get timestamp
 * @return {string}
 */
function timestamp() {
	const date = new Date();
	
	const year = date.getFullYear();
	const month = date.getMonth() + 1;
	const day = date.getDate();
	const hour = date.getHours();
	const minute = date.getMinutes();
	const second = date.getSeconds();
	
	return `${year}-${month}-${day}-${hour}-${minute}-${second}`;
}


/**
 * Update input names to prevent submitting data from hidden fields
 */
function update_input_names() {
	const $inputs = document.querySelectorAll(`${criteria_$} input`);
	$inputs.forEach($input => {
		const $hidden_parent = $input.closest(`[${g.hidden_attr}]`);
		
		// Input is within a hidden parent, remove its name attribute
		if (
			$hidden_parent
			&& $input.hasAttribute(input_name_attr)
		) {
			const name = $input.getAttribute(input_name_attr);
			$input.setAttribute(input_name_temp_attr, name);
			$input.removeAttribute(input_name_attr);
		}
		
		// Input is within a visible parent, restore its name attribute
		else if (
			!$hidden_parent
			&& $input.hasAttribute(input_name_temp_attr)
		) {
			const name = $input.getAttribute(input_name_temp_attr);
			$input.setAttribute(input_name_attr, name);
			$input.removeAttribute(input_name_temp_attr);
		}
	});
}


/**
 * Move fields to new location based on viewport width
 */
function move_fields() {
	const is_now_mobile = layout.isBreakpointMax(desktop_breakpoint);
	if (is_now_mobile === is_mobile) return false;
	
	is_mobile = is_now_mobile;
	
	const $containers = document.querySelectorAll(container_$);
	$containers.forEach($container => {
		const $fields_secondary = $container.querySelector(fields_secondary_$);
		if (!$fields_secondary) return false;
		
		const target_$ = `${field_container_secondary_$}${
			(is_mobile)
				? field_location_panel_$
				: field_location_body_$
		}`;
		const $target = $container.querySelector(target_$);
		if (!$target) return false;
		
		$target.append($fields_secondary);
	});
}
